import { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdClose, MdMailOutline, MdBlock, MdInfoOutline } from 'react-icons/md';
import DualDatePicker from '../common/DualDatePicker';
import { useConfirm } from '../common/ConfirmModal';
import useClubVocab from '../../hooks/useClubVocab';
import { useTenant } from '../../context/TenantContext';
import { hasBelts, hasClasses } from '../../utils/publicProfile';
import { directAddStudent, checkStudentNameExists } from '../../services/studentService';
import { getClasses } from '../../services/classService';
import { getSettings } from '../../services/settingsService';
import { sanitizePhone, isValidPhone, sanitizeEmail, isValidEmail, PHONE_HINT, EMAIL_HINT } from '../../utils/inputRules';
import { toWhatsAppPhone } from '../../utils/whatsapp';
import { ktmToday } from '../../utils/calendar';
import { calcAge, ageGroupFor, DEFAULT_AGE_GROUPS } from '../../utils/age';
import { BELT_ORDER } from '../../constants/belts';

const DISCIPLINES = ['Taekwondo','Kickboxing','Yoga','Fitness','Wrestling','Other'];
const DAYS_SHORT  = { Sun:'Sun',Mon:'Mon',Tue:'Tue',Wed:'Wed',Thu:'Thu',Fri:'Fri',Sat:'Sat',
  Sunday:'Sun',Monday:'Mon',Tuesday:'Tue',Wednesday:'Wed',Thursday:'Thu',Friday:'Fri',Saturday:'Sat' };

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const inputCls = (err) =>
  `w-full px-3 py-2.5 rounded-xl text-sm bg-nav-hover/40 border text-secondary placeholder:text-subtle
   focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors
   ${err ? 'border-red-500/50' : 'border-divider focus:border-primary/40'}`;

const labelCls = 'block text-xs font-medium text-muted mb-1';

// getClasses already attaches enrolledCount/capacity/capacitySet — plain text suffix since
// a native <option> can't be individually styled/coloured. capacity is null when the admin
// never set a limit — never invent a cap (no fake "(18/30)"), just the name in that case.
// Day/time suffix distinguishes same-named classes (e.g. multiple "Beginner Youth" sessions,
// one per weekday) — without it they're indistinguishable in a bare <select>.
const classOptionLabel = (c) => {
  const schedule = c.days?.length
    ? `${c.days.map(d => DAYS_SHORT[d] || d).join('/')}${c.startTime ? ` ${fmtTime(c.startTime)}` : ''}`
    : '';
  const base = schedule ? `${c.name} — ${schedule}` : c.name;
  if (!c.capacitySet) return base;
  const cap = c.capacity;
  const enrolled = c.enrolledCount ?? 0;
  return enrolled >= cap ? `${base} — Full (${enrolled}/${cap})` : `${base} (${enrolled}/${cap})`;
};

function Field({ label, required, error, children }) {
  return (
    <div>
      {label && (
        <label className={labelCls}>
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

/**
 * Add Student modal — supports BOTH a record-only student (no email / no login) and,
 * for admins, a normal login student (email + password). Trainers are locked to
 * record-only and never see credential or financial fields (also enforced server-side).
 */
export default function AddStudentModal({ isTrainer = false, onClose, onCreated }) {
  const v = useClubVocab();
  const { club } = useTenant();
  // Belt/grade + discipline are martial-arts concepts; hidden for gym/yoga/general. The same
  // flag flips "Parent / Guardian" → "Emergency Contact" (adults, not kids). Byte-identical for
  // taekwondo (hasBelts → true).
  const showBelts = hasBelts(club?.clubType);
  // Age group is a class-placement RECOMMENDATION — hidden entirely for gyms (no classes).
  const showAgeGroups = hasClasses(club?.clubType);
  // Trainers can only create record-only students.
  const [mode, setMode] = useState('record'); // 'record' | 'login'
  const isLogin = !isTrainer && mode === 'login';
  const [submitting, setSubmitting] = useState(false);
  const [confirm, confirmModal] = useConfirm();

  // Billing/onboarding (admin-only, optional): a real join date (defaults today) and the
  // first billable month. The billing month follows the join month until the admin overrides
  // it, so onboarding a student who joined months ago bills from the right month automatically.
  const [joinDate, setJoinDate]         = useState(() => ktmToday());
  const [feeStartDate, setFeeStartDate] = useState(() => ktmToday());
  const [feeTouched, setFeeTouched]     = useState(false);
  const handleJoinDate = (v) => {
    setJoinDate(v);
    if (!feeTouched && v) setFeeStartDate(v);
  };
  const handleFeeStartDate = (v) => { setFeeStartDate(v); setFeeTouched(true); };

  const { register, control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: { currentGrade: 'White', discipline: 'Taekwondo', gender: '', classId: '' },
  });

  // ── Age group (Stage A) — DERIVED live from DOB, never stored. Recommends classes only;
  // the admin still picks freely (no auto-select). ──────────────────────────────────────
  const dobValue = watch('dateOfBirth');
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn:  () => getSettings().then(r => r.data.settings),
    enabled:  showAgeGroups,
    staleTime: 1000 * 60 * 10,
  });
  const { data: classesData } = useQuery({
    queryKey: ['classes-all'],
    queryFn:  () => getClasses({ limit: 100 }).then(r => r.data.classes),
    enabled:  showAgeGroups,
    staleTime: 60000,
  });
  const ageGroups   = settingsData?.ageGroups?.length ? settingsData.ageGroups : DEFAULT_AGE_GROUPS;
  const currentAge  = dobValue ? calcAge(dobValue) : null;
  const ageGroup    = showAgeGroups ? ageGroupFor(currentAge, ageGroups) : null;
  // Under 18 → a parent/guardian phone is required (they're the real contact). 18+/unknown DOB
  // → optional, the student's own phone is enough.
  const guardianPhoneRequired = currentAge != null && currentAge < 18;
  const allClasses  = classesData || [];
  const recommendedClasses = ageGroup && !ageGroup.tooYoung
    ? allClasses.filter(c => c.suitableAgeGroups?.includes(ageGroup.name))
    : [];
  const recommendedIds  = new Set(recommendedClasses.map(c => c._id));
  const otherClasses    = allClasses.filter(c => !recommendedIds.has(c._id));

  // ── Advisory-only live duplicate-name check (does NOT block saving) ──────────
  const nameValue = watch('name');
  const [nameExists, setNameExists] = useState(false);
  const checkSeq = useRef(0);

  useEffect(() => {
    const n = (nameValue || '').trim();
    if (n.length < 3) { setNameExists(false); return; }
    const seq = ++checkSeq.current;
    const t = setTimeout(async () => {
      try {
        const res = await checkStudentNameExists(n);
        if (seq === checkSeq.current) setNameExists(!!res?.data?.exists);
      } catch { /* advisory only — silently ignore */ }
    }, 450);
    return () => clearTimeout(t);
  }, [nameValue]);

  const onSubmit = async (vals) => {
    setSubmitting(true);
    try {
      const payload = {
        name:         vals.name?.trim(),
        phone:        vals.phone?.trim() || undefined,
        dateOfBirth:  vals.dateOfBirth || undefined,
        gender:       vals.gender || undefined,
        currentGrade: vals.currentGrade || undefined,
        discipline:   vals.discipline || undefined,
        classId:      showAgeGroups ? (vals.classId || undefined) : undefined,
        guardianName:  vals.guardianName?.trim() || undefined,
        guardianPhone: vals.guardianPhone?.trim() || undefined,
        guardianEmail: vals.guardianEmail?.trim() || undefined,
        medicalInfo:   vals.medicalInfo?.trim() || undefined,
        // Contact-only reminder email (record-only members). Never a login.
        contactEmail:  !isLogin ? (vals.contactEmail?.trim() || undefined) : undefined,
      };
      if (isLogin) {
        payload.email    = vals.email?.trim();
        payload.password = vals.password;
      }
      // Admin-only fields (never sent by trainers; also stripped server-side).
      if (!isTrainer) {
        if (vals.monthlyFee !== '' && vals.monthlyFee != null) payload.monthlyFee = Number(vals.monthlyFee);
        if (vals.notes?.trim()) payload.notes = vals.notes.trim();
        if (joinDate)     payload.joinDate     = joinDate;     // AD YYYY-MM-DD
        if (feeStartDate) payload.feeStartDate = feeStartDate; // AD YYYY-MM-DD — real day, never truncated
      }

      let res;
      try {
        res = await directAddStudent(payload);
      } catch (e) {
        // Chosen class is full — offer to add anyway rather than failing outright.
        if (e.response?.status === 409 && e.response?.data?.code === 'CLASS_FULL') {
          const ok = await confirm({
            title: 'Class is full',
            message: e.response.data.message,
            confirmLabel: 'Add anyway',
            destructive: false,
          });
          if (!ok) { setSubmitting(false); return; }
          res = await directAddStudent({ ...payload, confirmCapacityOverride: true });
        } else {
          throw e;
        }
      }
      toast.success(
        res?.data?.student?.isRecordOnly
          ? `Record-only ${v.member.toLowerCase()} added`
          : `${v.member} added — they can log in with their email`
      );
      reset();
      setJoinDate(ktmToday());
      setFeeStartDate(ktmToday());
      setFeeTouched(false);
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.message || `Failed to add ${v.member.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-card rounded-3xl shadow-2xl border border-divider w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header (sticky) */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-divider flex-shrink-0">
          <div>
            <h3 className="font-bold text-secondary text-lg">Add {v.member}</h3>
            <p className="text-xs text-muted mt-0.5">
              {isTrainer ? `Add a ${v.member.toLowerCase()} to track (no login).` : `Create a tracked ${v.member.toLowerCase()}, with or without app access.`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-muted hover:text-secondary hover:bg-nav-hover rounded-lg transition-colors">
            <MdClose className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col min-h-0 flex-1">
          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-3">
          {/* Mode toggle (admins only) */}
          {!isTrainer ? (
            <div className="flex gap-1 bg-nav-hover/40 p-1 rounded-xl">
              {[
                { key: 'record', label: 'No email (record only)', Icon: MdBlock },
                { key: 'login',  label: 'Has email (app access)', Icon: MdMailOutline },
              ].map(o => {
                const active = mode === o.key;
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setMode(o.key)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                      active ? 'bg-card border border-divider shadow-sm text-secondary' : 'text-muted hover:text-secondary'
                    }`}
                  >
                    <o.Icon className={`text-sm ${active ? 'text-primary' : ''}`} /> {o.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-start gap-2 text-xs text-muted bg-nav-hover/40 border border-divider rounded-xl px-3 py-2.5">
              <MdInfoOutline className="text-base flex-shrink-0 mt-0.5 text-primary" />
              This {v.member.toLowerCase()} is tracked for attendance, belts and fees but has no login. An admin can add app access later from the {v.member.toLowerCase()}'s profile.
            </div>
          )}

          {/* Identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full Name" required error={errors.name?.message}>
              <input placeholder="e.g. Anusha Dhakal" className={inputCls(errors.name)}
                {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'At least 2 characters' } })} />
              {nameExists && (
                <div className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
                  <MdInfoOutline className="text-sm flex-shrink-0 mt-0.5" />
                  <span>A {v.member.toLowerCase()} with this name already exists — add a nickname or detail to tell them apart.</span>
                </div>
              )}
            </Field>
            <Field label="Phone" error={errors.phone?.message}>
              <input type="tel" placeholder="97/98XXXXXXXX" className={inputCls(errors.phone)}
                {...register('phone', { validate: (v) => isValidPhone(v) || PHONE_HINT })}
                onChange={(e) => setValue('phone', sanitizePhone(e.target.value), { shouldValidate: true })} />
              <p className="text-[11px] text-subtle mt-1">Doubles as their WhatsApp number for reminders.</p>
            </Field>

            {/* Contact-only reminder email — record-only members (no login) still get reminders/receipts */}
            {!isLogin && (
              <Field label="Email for reminders (optional)" error={errors.contactEmail?.message}>
                <input type="email" placeholder="email@example.com" className={inputCls(errors.contactEmail)}
                  {...register('contactEmail', { validate: (val) => !val || isValidEmail(val) || EMAIL_HINT })}
                  onChange={(e) => setValue('contactEmail', sanitizeEmail(e.target.value), { shouldValidate: true })} />
                <p className="text-[11px] text-subtle mt-1">They&apos;ll get fee reminders and receipts — but no login access.</p>
              </Field>
            )}

            {isLogin && (
              <>
                <Field label="Email" required error={errors.email?.message}>
                  <input type="email" placeholder="email@example.com" className={inputCls(errors.email)}
                    {...register('email', {
                      required: 'Email is required for app access',
                      validate: (v) => isValidEmail(v) || EMAIL_HINT,
                    })}
                    onChange={(e) => setValue('email', sanitizeEmail(e.target.value), { shouldValidate: true })} />
                </Field>
                <Field label="Temporary Password" required error={errors.password?.message}>
                  <input type="password" placeholder="Min. 8 characters" className={inputCls(errors.password)}
                    {...register('password', {
                      required: 'Password is required for app access',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })} />
                </Field>
              </>
            )}

            <div>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <DualDatePicker label="Date of Birth" value={field.value || ''} onChange={field.onChange} max={ktmToday()} fastNav />
                )}
              />
              {showAgeGroups && dobValue && (
                <p className="text-[11px] mt-1.5">
                  {currentAge == null ? (
                    <span className="text-amber-500">Invalid date of birth</span>
                  ) : (
                    <>
                      <span className="text-muted">{currentAge} yrs</span>
                      {ageGroup && (
                        <span className={`ml-1.5 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          ageGroup.tooYoung ? 'bg-nav-hover text-muted' : 'bg-nav-hover text-secondary'
                        }`}>
                          {ageGroup.name}
                        </span>
                      )}
                    </>
                  )}
                </p>
              )}
            </div>
            <Field label="Gender">
              <select className={inputCls()} {...register('gender')}>
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </Field>
            {/* Belt & Discipline — martial-arts only; hidden for gym/yoga/general */}
            {showBelts && (
              <>
                <Field label="Current Belt / Grade">
                  <select className={inputCls()} {...register('currentGrade')}>
                    {BELT_ORDER.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
                <Field label="Discipline">
                  <select className={inputCls()} {...register('discipline')}>
                    {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
              </>
            )}
          </div>

          {/* Class (optional) — recommended-by-age-group classes listed first; the admin still
              picks freely, nothing is ever pre-selected. Hidden entirely for gyms (no classes). */}
          {showAgeGroups && (
            <Field label="Class (optional)">
              <select className={inputCls()} {...register('classId')}>
                <option value="">No class yet — assign later</option>
                {recommendedClasses.length > 0 && (
                  <optgroup label={`Recommended for ${ageGroup.name}`}>
                    {recommendedClasses.map(c => <option key={c._id} value={c._id}>{classOptionLabel(c)}</option>)}
                  </optgroup>
                )}
                <optgroup label={recommendedClasses.length > 0 ? 'Other classes' : 'All classes'}>
                  {otherClasses.map(c => <option key={c._id} value={c._id}>{classOptionLabel(c)}</option>)}
                </optgroup>
              </select>
              <p className="text-[11px] text-subtle mt-1">
                {ageGroup && !ageGroup.tooYoung && recommendedClasses.length === 0
                  ? `No classes tagged for ${ageGroup.name} yet — pick any class below, or leave blank and assign one later.`
                  : 'Optional — you can assign a class later from the student profile instead.'}
              </p>
            </Field>
          )}

          {/* Guardian / emergency contact — label depends on club type (same underlying fields) */}
          <div className="pt-1">
            <p className="text-xs font-semibold text-muted mb-2">
              {(showBelts ? 'Parent / Guardian' : 'Emergency Contact') + (guardianPhoneRequired ? '' : ' (optional)')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Name"><input className={inputCls()} {...register('guardianName')} /></Field>
              <Field label={guardianPhoneRequired ? 'Phone *' : 'Phone'} error={errors.guardianPhone?.message}>
                <input type="tel" className={inputCls(errors.guardianPhone)}
                  {...register('guardianPhone', { validate: (v) => {
                    if (guardianPhoneRequired) {
                      return toWhatsAppPhone(v) !== null || 'A parent/guardian phone is required for students under 18';
                    }
                    return isValidPhone(v) || PHONE_HINT;
                  } })}
                  onChange={(e) => setValue('guardianPhone', sanitizePhone(e.target.value), { shouldValidate: true })} />
              </Field>
              <Field label="Email" error={errors.guardianEmail?.message}>
                <input type="email" className={inputCls(errors.guardianEmail)}
                  {...register('guardianEmail', { validate: (v) => isValidEmail(v) || EMAIL_HINT })}
                  onChange={(e) => setValue('guardianEmail', sanitizeEmail(e.target.value), { shouldValidate: true })} />
              </Field>
            </div>
          </div>

          {/* Medical */}
          <Field label="Medical Conditions / Injuries (optional)">
            <textarea rows={2} placeholder="Anything trainers should know for safety"
              className={`${inputCls()} resize-none`} {...register('medicalInfo')} />
          </Field>

          {/* Admin-only: financial + private notes */}
          {!isTrainer && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <Field label="Monthly Fee Override (optional)">
                <input type="number" min="0" placeholder="Club default if blank" className={inputCls()} {...register('monthlyFee')} />
              </Field>
              <Field label="Admin Notes (private)">
                <input placeholder="Internal note" className={inputCls()} {...register('notes')} />
              </Field>
            </div>
          )}

          {/* Admin-only: join date + billing start — for onboarding students who joined earlier */}
          {!isTrainer && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <DualDatePicker
                  label="Joined date"
                  value={joinDate}
                  onChange={handleJoinDate}
                  max={ktmToday()}
                  fastNav
                />
                <p className="text-[11px] text-subtle mt-1">When they joined the club — pick the 1st if unsure of the exact day</p>
              </div>
              <div>
                <DualDatePicker
                  label="Billing starts from"
                  value={feeStartDate}
                  onChange={handleFeeStartDate}
                  max={ktmToday()}
                  fastNav
                />
                <p className="text-[11px] text-subtle mt-1">Defaults to the joined date — their first charge is prorated for a partial month unless this is the 1st</p>
              </div>
            </div>
          )}

          </div>

          {/* Actions (sticky footer) */}
          <div className="flex-shrink-0 flex gap-3 px-6 py-3 border-t border-divider">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-divider text-sm font-medium text-secondary hover:bg-nav-hover transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark disabled:opacity-60 transition-colors">
              {submitting ? 'Adding…' : `Add ${v.member}`}
            </button>
          </div>
        </form>
      </div>
      {confirmModal}
    </div>
  );
}
